import React/* , { useState }  */from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { MdMoreVert } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { Popconfirm, Popover, Menu } from 'antd';
import moment from 'moment-timezone';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';

import Button from '../common/Button';
import Loading from '../common/Loading';
// import TicketModal, { SingleTicketEditorModal } from './MemberEventTicketModal';
import { setLoading } from '../../redux/app/appSlice';
import useMemberEventTickets from '../../hooks/useMemberEventTickets';
import useRevokeMemberEventTicket from '../../hooks/useRevokeMemberEventTicket';
import useRefundMemberEventTicket from '../../hooks/useRefundMemberEventTicket';
import {
  Card,
  Title,
} from '../common/elements';
import {
  $medium,
  $gray,
  $lightGold,
  $steelBlue,
} from '../../styles';
import { formatNum } from '../../utils/currency';

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Row = styled.div`
  padding: 1rem 0.5rem;
  border-top: solid 1px ${$gray};
  > svg {
    &:last-child {
      margin-left: auto;
    }
  }
  ${({ disabled }) => (disabled === 'true' && `
    opacity: 0.65;
    background: ${$lightGold};
    pointer-events: none;
  ;`)
}
`;

const GuestDetails = styled.div`
  display: flex;
  align-items: center;
`;

const TicketType = styled.div`
  color: ${$steelBlue};
  font-weight: ${$medium};
  margin-bottom: 0.5rem;
`;

const PersonalDetails = styled.div`
  min-width: 30%;
  margin-right: 1rem;
`;

const TicketSectionTitle = styled.div`
  font-weight: ${$medium};
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const PersonalDetail = styled.div`
  // margin: 0.5rem 0;
`;

const MoreOptionsButtonContainer = styled.div`
  padding: 1rem 0.5rem 1rem 1rem;
  margin-left: auto;
`;

const MoreOptionsButton = styled(MdMoreVert)`
  font-size: 1.5rem;
  cursor: pointer;
`;

const TimeIndicator = styled.div`
  font-style: italic;
`;

const GuestsContainer = styled.div`
  // max-height: 21rem;
  overflow: auto;
  ${({ disabled }) => disabled && `
    opacity: 0.7;
    pointer-events: none;
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  margin-bottom: 1rem;
  font-style: italic;
`;

const Ticket = (props) => {
  const {
    ticket,
    onRevoke,
    onRefund,
    // onEdit,
  } = props;
  const {
    id,
    name,
    email,
    purchaser,
    createdAt,
    creditRedemptionByCreditRedemptionId: creditRedemption,
    purchaseByPurchaseId: purchase,
    ticketType,
  } = ticket;

  let purchaserName;
  let purchaserEmail;

  if (purchaser) {
    purchaserName = `${purchaser.givenNames} ${purchaser.familyName}`;
    purchaserEmail = purchaser.email;
  }

  const ticketDropdown = (
    <Menu>
      {
        creditRedemption ? (
          <Menu.Item>
            <Popconfirm
              placement="right"
              title="Are you sure?"
              onConfirm={() => onRefund({ id })}
              okText="Yes"
              cancelText="No"
            >
              <div>Refund</div>
            </Popconfirm>
          </Menu.Item>
        ) : (
          <Menu.Item disabled className="admin-ticket-option-disabled">
            <div>Refund</div>
          </Menu.Item>
        )
      }
      <Menu.Item>
        <Popconfirm
          placement="right"
          title="Are you sure?"
          onConfirm={() => onRevoke({ id })}
          okText="Yes"
          cancelText="No"
        >
          <div>Revoke</div>
        </Popconfirm>
      </Menu.Item>
      {/* <Menu.Item onClick={() => onEdit({ id, name, email })}>
        <div>Edit</div>
      </Menu.Item> */}
      {
        creditRedemption && (
          <Menu.Item>
            <Link to={`/redemption/${creditRedemption.id}`} target="_blank">View receipt</Link>
          </Menu.Item>
        )
      }
      {
        purchase && (
          <Menu.Item>
            <Link to={`/purchase/${purchase.id}`} target="_blank">View receipt</Link>
          </Menu.Item>
        )
      }
    </Menu>
  );
  return (
    <Row key={id}>
      <TicketType>{ `#${id} - ${ticketType}` }</TicketType>
      <GuestDetails>
        <PersonalDetails>
          {
            name || email ?
              (
                <>
                  <TicketSectionTitle>
                    Guest:
                  </TicketSectionTitle>
                  <PersonalDetail>{ name }</PersonalDetail>
                  <PersonalDetail>{ email }</PersonalDetail>
                </>
              ) : <TicketSectionTitle>Ticket unclaimed</TicketSectionTitle>
          }
        </PersonalDetails>
        <PersonalDetails>
          <TicketSectionTitle>
            {
              purchaser && purchaser.isGuest ? 'Purchaser (non-member):' : 'Purchaser:'
            }
          </TicketSectionTitle>
          <PersonalDetail>{ purchaserName || 'Admin' }</PersonalDetail>
          {
            purchaserEmail && <PersonalDetail>{ purchaserEmail }</PersonalDetail>
          }
        </PersonalDetails>
        <TimeIndicator>{ `Purchased ${moment(createdAt).fromNow()}` }</TimeIndicator>
        <Popover
          content={ticketDropdown}
          trigger="click"
          placement="right"
          align={{
            offset: ['-1rem', 0],
          }}
          overlayClassName="admin-ticket-more-options"
        >
          <MoreOptionsButtonContainer>
            <MoreOptionsButton />
          </MoreOptionsButtonContainer>
        </Popover>
      </GuestDetails>
    </Row>
  );
};

Ticket.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.number,
    email: PropTypes.string,
    name: PropTypes.string,
    purchaser: PropTypes.shape({
      givenNames: PropTypes.string,
      familyName: PropTypes.string,
      email: PropTypes.string,
      isGuest: PropTypes.bool,
    }),
    createdAt: PropTypes.string,
    creditRedemptionByCreditRedemptionId: PropTypes.shape({
      id: PropTypes.number,
    }),
    purchaseByPurchaseId: PropTypes.shape({
      id: PropTypes.number,
    }),
    invitationByInvitationId: PropTypes.shape({
      email: PropTypes.string,
      name: PropTypes.string,
    }),
    ticketType: PropTypes.string,
  }).isRequired,
  onRevoke: PropTypes.func.isRequired,
  onRefund: PropTypes.func.isRequired,
  // onEdit: PropTypes.func.isRequired,
};

const Tickets = (props) => {
  const {
    tickets,
    revokeTicket,
    refundTicket,
    // editTicket,
    disabled,
  } = props;
  return (
    <GuestsContainer disabled={disabled}>
      {
        tickets.length === 0 && <EmptyState>No tickets sold...yet</EmptyState>
      }
      {
        tickets.map((ticket) => (
          <Ticket
            key={ticket.id}
            ticket={ticket}
            onRevoke={revokeTicket}
            onRefund={refundTicket}
            // onEdit={editTicket}
          />
        ))
      }
    </GuestsContainer>
  );
};

Tickets.propTypes = {
  tickets: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
  })).isRequired,
  revokeTicket: PropTypes.func.isRequired,
  refundTicket: PropTypes.func.isRequired,
  // editTicket: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

Tickets.defaultProps = {
  disabled: false,
};

const MemberEventGuestManager = (props) => {
  const dispatch = useDispatch();
  // const [showTicketModal, setShowTicketModal] = useState(false);
  // const closeTicketModal = () => setShowTicketModal(false);

  // const [ticketToEdit, setTicketToEdit] = useState(null);

  const { memberEvent } = props;

  const {
    // id: memberEventId,
    name: memberEventName,
    maxCapacity,
  } = memberEvent;

  // Load & ingest member event data
  const {
    refetch,
    data: tickets,
    loading,
  } = useMemberEventTickets(memberEvent.id);

  const [revokeMemberEventTicket, { loading: revoking }] = useRevokeMemberEventTicket();
  const [refundMemberEventTicket, { loading: refunding }] = useRefundMemberEventTicket();

  const revoke = async (ticket) => {
    dispatch(setLoading(true));
    try {
      await revokeMemberEventTicket(ticket);
      await refetch();
      toast.success('Ticket revoked');
      dispatch(setLoading(false));
    } catch (e) {
      toast.error(e.message);
      dispatch(setLoading(false));
    }
  };

  const refund = async (ticket) => {
    dispatch(setLoading(true));
    try {
      const data = await refundMemberEventTicket(ticket);
      await refetch();
      toast.success(`Ticket revoked & ${data.amount} credits refunded`);
      dispatch(setLoading(false));
    } catch (e) {
      toast.error(e.message);
      dispatch(setLoading(false));
    }
  };

  const downloadAttendees = () => {
    const formattedAttendees = tickets.map((ticket) => {
      const {
        id,
        name,
        email,
        purchaser,
        ticketType,
      } = ticket;
      let purchaserName = 'Admin';
      let purchaserEmail = 'N/A';
      if (purchaser) {
        purchaserName = `${purchaser.givenNames} ${purchaser.familyName}`;
        purchaserEmail = purchaser.email;
      }
      return {
        ID: id,
        Type: ticketType,
        'Guest Name': name,
        'Guest Email': email,
        'Purchaser Name': purchaserName,
        'Purchaser Email': purchaserEmail,
      };
    });

    const csv = Papa.unparse(formattedAttendees);
    const blob = new Blob([csv], { type: 'text/plain;charset=utf-8' });
    const fileName = `Attendees for ${memberEventName}.csv`;
    saveAs(blob, fileName);
  };

  return (
    <>
      <Card disableHover>
        <Header>
          <Title margin="0 0 0 0">
            TICKETS
            { !loading && ` (${formatNum(tickets.length)} / ${formatNum(maxCapacity)} sold)` }
          </Title>
          <Toolbar>
            <Button margin="0 1rem 0 0" onClick={downloadAttendees} theme="blackOutline">DOWNLOAD CSV</Button>
            {/* <Button onClick={() => setShowTicketModal(true)}>ADD GUESTS</Button> */}
          </Toolbar>
        </Header>
        {
          loading ? <Loading centerSelf /> : (
            <Tickets
              tickets={tickets}
              // editTicket={setTicketToEdit}
              revokeTicket={revoke}
              refundTicket={refund}
              disabled={loading || revoking || refunding}
            />
          )
        }
        {/* <TicketModal
          visible={showTicketModal}
          closeModal={closeTicketModal}
          memberEventId={memberEventId}
          onCompleted={() => refetch()}
        />
        {
          ticketToEdit && (
            <SingleTicketEditorModal
              visible={!!ticketToEdit}
              closeModal={() => setTicketToEdit(null)}
              onCompleted={() => refetch()}
              ticket={ticketToEdit}
            />
          )
        } */}
      </Card>
    </>
  );
};

MemberEventGuestManager.propTypes = {
  memberEvent: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    maxCapacity: PropTypes.number.isRequired,
  }).isRequired,
};

export default MemberEventGuestManager;
